import React from 'react';

import type {
  ComponentRenderProps,
  SourceListProps,
} from '../../../types/dynamic_views';
import { useManifestId } from '../ManifestContext';

// Sanitize URL to prevent XSS attacks
const sanitizeUrl = (url: string): string => {
  try {
    const parsedUrl = new URL(url);
    // Only allow safe protocols
    if (['http:', 'https:'].includes(parsedUrl.protocol)) {
      return url;
    }
    // Unsafe protocol detected - return empty string
    return '';
  } catch {
    // Malformed URL - return empty string
    return '';
  }
};

export const SourceListComponent: React.FC<ComponentRenderProps> = ({
  component,
  className = '',
}) => {
  const props = component.props as SourceListProps;
  const { items, spacing = 'normal' } = props;
  const manifestId = useManifestId();

  // Build CSS classes
  const spacingClasses = {
    tight: 'space-y-2',
    normal: 'space-y-3',
    loose: 'space-y-4',
  };

  const allClasses = [
    spacingClasses[spacing] || spacingClasses.normal,
    'list-none',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  // Validate data
  if (!items || !Array.isArray(items) || items.length === 0) {
    return null;
  }

  return (
    <ol className={allClasses} data-component-id={component.id}>
      {items.map((item, index) => {
        // Resolve number: use explicit number or auto-assign from index
        const number = item.number ?? index + 1;

        // Title is normalized on save by the backend; fall back defensively
        const title = item.title || 'Untitled';

        // Sanitize URL to prevent XSS
        const sanitizedUrl = sanitizeUrl(item.url);

        // Extract domain from sanitized URL (only if sanitization succeeded)
        let domain = '';
        if (sanitizedUrl) {
          try {
            domain = new URL(sanitizedUrl).hostname.replace('www.', '');
          } catch {
            // Malformed sanitized URL - set empty domain
            domain = '';
          }
        }

        const sourceId = manifestId
          ? `${manifestId}-source-${number}`
          : `source-${number}`;

        // If URL was sanitized to empty (unsafe protocol), display warning
        if (!sanitizedUrl) {
          return (
            <li
              key={`source-${number}`}
              id={sourceId}
              data-source-number={number}
              role='doc-endnote'
              tabIndex={0}
              className='leading-relaxed text-yellow-600 text-sm'
            >
              <span className='text-fg-quaternary text-xs font-medium mr-1.5'>
                [{number}]
              </span>
              <span>{title}</span>
              <span className='text-xs ml-1.5'>
                (unsafe or malformed URL: {item.url})
              </span>
            </li>
          );
        }

        return (
          <li
            key={`source-${number}`}
            id={sourceId}
            data-source-number={number}
            role='doc-endnote'
            tabIndex={0}
            className='text-sm leading-relaxed transition-colors duration-500'
          >
            <span className='text-fg-quaternary text-xs font-medium mr-1.5'>
              [{number}]
            </span>
            <a
              href={sanitizedUrl}
              target='_blank'
              rel='noopener noreferrer'
              aria-label={`${title} (opens in new tab)`}
              className='text-utility-brand-600 dark:text-utility-brand-700 hover:underline font-medium'
            >
              {title}
            </a>
            <span className='text-fg-quaternary text-xs ml-1.5'>
              · {domain}
            </span>
          </li>
        );
      })}
    </ol>
  );
};

export default SourceListComponent;
